import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import FWSection from "../../design-system/fw/FWSection";
import {
  fetchAttractionRulesForPackage,
  fetchPackageAttractionLines,
} from "../../services/attractionsApi";
import AttractionsUpsellBlock from "../AttractionsUpsellBlock/AttractionsUpsellBlock";
import {
  buildRuleByAttractionMap,
  dedupeRulesByAttractionId,
  getBillableAttractionsTotalGross,
  partitionRulesForPackageProfile,
} from "../../utils/packageAttractionRulesDisplay";
import "./PackageAttractionsProfileSection.scss";

/**
 * בלוק מאוחד: הטבות כלולות, מתנות, שדרוגים בתשלום (רק חייבים).
 *
 * @param {{ variant?: 'default' | 'flightProposal' }} [props]
 * - default — פרופיל לקוח (כותרת + disclaimer מלא).
 * - flightProposal — עטיפת FWSection לסוכן; ללא כפילות כותרת; disclaimer קצר.
 */
export default function PackageAttractionsProfileSection({
  variant = "default",
  dynamicPackageId,
  destinationId = null,
  packageKey = null,
  flightScopeValue = null,
  visitDate = null,
  dateFrom = null,
  dateTo = null,
  partySize = 1,
  currencyLabel = "₪",
  upgradesSectionTitle = "שדרוגים מומלצים",
  flightSectionTitle = "חוויות והטבות בחבילה",
}) {
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [linesPayload, setLinesPayload] = useState(null);
  const [rulesError, setRulesError] = useState(null);

  const [upsellSnap, setUpsellSnap] = useState({
    loading: true,
    upgradeItemsLength: 0,
    itemsLength: 0,
    error: null,
  });

  const handleUpsellStateChange = useCallback((s) => {
    setUpsellSnap({
      loading: s.loading,
      upgradeItemsLength: s.upgradeItemsLength,
      itemsLength: s.itemsLength,
      error: s.error,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (dynamicPackageId == null || Number.isNaN(Number(dynamicPackageId))) {
      setRulesLoading(false);
      setRules([]);
      setLinesPayload(null);
      return undefined;
    }
    const dpid = Number(dynamicPackageId);
    setRulesLoading(true);
    setRulesError(null);
    (async () => {
      try {
        const [rulesRes, linesRes] = await Promise.all([
          fetchAttractionRulesForPackage({
            dynamic_package_id: dpid,
            destination_id: destinationId ?? undefined,
            package_key: packageKey || undefined,
            flight_scope_value: flightScopeValue || undefined,
            visit_date: visitDate || undefined,
            limit: 80,
          }),
          fetchPackageAttractionLines(dpid).catch(() => ({ lines: [], meta: {} })),
        ]);
        if (cancelled) return;
        setRules(Array.isArray(rulesRes?.rules) ? rulesRes.rules : []);
        setLinesPayload(linesRes && typeof linesRes === "object" ? linesRes : null);
      } catch (e) {
        if (!cancelled) {
          setRules([]);
          setLinesPayload(null);
          setRulesError(e?.response?.data?.message || e?.message || "טעינת אטרקציות נכשלה");
        }
      } finally {
        if (!cancelled) setRulesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dynamicPackageId, destinationId, packageKey, flightScopeValue, visitDate]);

  const deduped = useMemo(() => dedupeRulesByAttractionId(rules), [rules]);
  const { included, gifts } = useMemo(
    () => partitionRulesForPackageProfile(deduped),
    [deduped]
  );
  const ruleByAttractionId = useMemo(
    () => buildRuleByAttractionMap(deduped),
    [deduped]
  );

  const billableExtra = useMemo(
    () => getBillableAttractionsTotalGross(linesPayload || {}),
    [linesPayload]
  );

  const hasIncluded = included.length > 0;
  const hasGifts = gifts.length > 0;

  const showUpgradesSubsection = useMemo(() => {
    return (
      billableExtra > 0 ||
      upsellSnap.loading ||
      upsellSnap.upgradeItemsLength > 0 ||
      (upsellSnap.error != null && upsellSnap.error !== "")
    );
  }, [
    billableExtra,
    upsellSnap.loading,
    upsellSnap.upgradeItemsLength,
    upsellSnap.error,
  ]);

  const fullyEmpty =
    !rulesLoading &&
    !rulesError &&
    !hasIncluded &&
    !hasGifts &&
    billableExtra <= 0 &&
    !showUpgradesSubsection;

  if (dynamicPackageId == null || Number.isNaN(Number(dynamicPackageId))) {
    return null;
  }

  if (fullyEmpty) {
    return null;
  }

  const embedded = variant === "flightProposal";
  const rootClass = `pkg-attractions-profile${embedded ? " pkg-attractions-profile--embedded" : ""}`;

  const body = (
    <div className={rootClass} aria-label="חוויות והטבות בחבילה">
      {!embedded ? (
        <h3 className="pkg-attractions-profile__heading">חוויות והטבות בחבילה</h3>
      ) : null}

      {rulesLoading ? (
        <Spin size="small" />
      ) : rulesError ? (
        <p className="pkg-attractions-profile__disclaimer" role="alert">
          {rulesError}
        </p>
      ) : (
        <>
          {hasIncluded ? (
            <>
              <h4 className="pkg-attractions-profile__sub">הטבות הכלולות בחבילה</h4>
              <p className="pkg-attractions-profile__included-note">
                מוצגות למטרות שיווק; אינן מוסיפות למחיר החבילה ששילמתם.
              </p>
              <ul className="pkg-attractions-profile__list">
                {included.map((r) => {
                  const badge =
                    r?.badge_text != null && String(r.badge_text).trim()
                      ? String(r.badge_text).trim()
                      : null;
                  const text =
                    r?.display_text != null && String(r.display_text).trim()
                      ? String(r.display_text).trim()
                      : badge || r?.name || "הטבה";
                  return (
                    <li key={r.id} className="pkg-attractions-profile__item">
                      {badge ? (
                        <span className="pkg-attractions-profile__badge pkg-attractions-profile__badge--included">
                          {badge}
                        </span>
                      ) : null}
                      <span>{text}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}

          {hasGifts ? (
            <>
              <h4 className="pkg-attractions-profile__sub">מתנות</h4>
              <ul className="pkg-attractions-profile__list">
                {gifts.map((r) => {
                  const badge =
                    r?.badge_text != null && String(r.badge_text).trim()
                      ? String(r.badge_text).trim()
                      : null;
                  const text =
                    r?.display_text != null && String(r.display_text).trim()
                      ? String(r.display_text).trim()
                      : badge || r?.name || "מתנה";
                  const auto = r?.auto_attach === true || r?.auto_attach === "true";
                  return (
                    <li key={r.id} className="pkg-attractions-profile__item">
                      {badge ? (
                        <span className="pkg-attractions-profile__badge pkg-attractions-profile__badge--gift">
                          {badge}
                        </span>
                      ) : null}
                      <span>{text}</span>
                      {auto ? (
                        <span className="pkg-attractions-profile__badge pkg-attractions-profile__badge--included">
                          כלול בהזמנה
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}

          {showUpgradesSubsection ? (
            <>
              <h4 className="pkg-attractions-profile__sub">{upgradesSectionTitle}</h4>
              {billableExtra > 0 ? (
                <div className="pkg-attractions-profile__billable-pill" aria-live="polite">
                  תוספות בתשלום שכבר נוספו להזמנה: {currencyLabel}
                  {Number(billableExtra).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                </div>
              ) : null}
              <div className="pkg-attractions-profile__upgrades-wrap">
                <AttractionsUpsellBlock
                  context="package"
                  targetType="dynamic_package"
                  targetId={Number(dynamicPackageId)}
                  destinationId={destinationId}
                  visitDate={visitDate}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  partySize={partySize}
                  currencyLabel={currencyLabel}
                  title={upgradesSectionTitle}
                  packageRulesByAttractionId={ruleByAttractionId}
                  hideBlockTitle
                  suppressEmptyDisplay
                  onUpsellStateChange={handleUpsellStateChange}
                />
              </div>
            </>
          ) : null}

          {!embedded ? (
            <p className="pkg-attractions-profile__disclaimer">
              מתנות והטבות שיווקיות אינן משנות את מחיר החבילה. סכום תוספות חייב מחושב לפי נתוני השרת
              (billable), ולא לפי סכימה ישירה של מחירי שורות ללא סינון.
            </p>
          ) : (
            <p className="pkg-attractions-profile__disclaimer pkg-attractions-profile__disclaimer--agent">
              מתנות והטבות לתצוגה בלבד; שדרוגים בתשלום מופיעים למטה בלבד.
            </p>
          )}
        </>
      )}
    </div>
  );

  if (embedded) {
    return <FWSection title={flightSectionTitle}>{body}</FWSection>;
  }

  return body;
}
